import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FloatingCTA from "@/components/feature/FloatingCTA";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SEO from "@/components/feature/SEO";
import { SupabaseConnectionStatus } from "@/components/feature/SupabaseConnectionStatus";
import WhatsAppWidget from "@/components/feature/WhatsAppWidget";
import { webpageSchema, breadcrumbSchema, siteUrl } from "@/lib/schemas";
import { supabase, isSupabaseConnected, supabaseFunctionsBaseUrl, supabaseAnonKeyClean } from "@/lib/supabase";
import { trackEvent } from "@/lib/gtag";
import { Calendar, Clock, Phone, Mail, MapPin, User, FileText, CheckCircle, ArrowRight } from "lucide-react";

export default function BookAppointmentPage() {
  const [searchParams] = useSearchParams();
  const pageSource = searchParams.get("source") || "Book Appointment Page";

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    insuranceNeed: "health",
    preferredDate: "",
    preferredTime: "morning",
    message: "",
  });
  const baseUrl = siteUrl;
  const pageUrl = `${baseUrl}/book-appointment`;

  const schemas = [
    webpageSchema(
      "Book Free Consultation | ExpertAdvise.in Insurance Advisor",
      "Book a free consultation with ExpertAdvise.in certified insurance advisors. Get personalized recommendations for health, term, life, car, and investment plans.",
      pageUrl
    ),
    breadcrumbSchema([
      { name: "Home", url: baseUrl },
      { name: "Book Appointment", url: pageUrl },
    ]),
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError("");
    console.log("[Book Appointment] Submit started");

    if (!isSupabaseConnected || !supabase) {
      console.error("[Book Appointment] Supabase NOT connected — isSupabaseConnected:", isSupabaseConnected, "supabase:", !!supabase);
      setSubmitting(false);
      setSubmitError("Unable to connect to our system right now. Please call us at +91 99993 65655 or WhatsApp us.");
      return;
    }

    console.log("[Book Appointment] Supabase connected, attempting insert...");
    try {
      const { data: insertData, error } = await supabase.from("appointments").insert({
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        city: formData.city,
        insurance_type: formData.insuranceNeed,
        preferred_date: formData.preferredDate,
        preferred_time: formData.preferredTime,
        notes: formData.message,
        source: pageSource,
        status: "scheduled",
      }).select();

      console.log("[Book Appointment] Insert result:", { insertData, error });

      if (error) {
        console.error("[Book Appointment] Insert error:", error);
        setSubmitting(false);
        setSubmitError(error.message || "Something went wrong saving your appointment. Please try again or call +91 99993 65655.");
        return;
      }

      console.log("[Book Appointment] Insert succeeded, data:", insertData);

      // Notify admins via edge function (fire-and-forget)
      try {
        const edgeUrl = `${supabaseFunctionsBaseUrl}/functions/v1/send-appointment-notification`;
        await fetch(edgeUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${supabaseAnonKeyClean}`,
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            city: formData.city,
            insurance_type: formData.insuranceNeed,
            preferred_date: formData.preferredDate,
            preferred_time: formData.preferredTime,
            notes: formData.message,
            source: pageSource,
          }),
        });
      } catch {
        // Silently ignore notification failures
      }

      setSubmitting(false);
      setSubmitted(true);
      trackEvent("book_appointment", {
        insurance_type: formData.insuranceNeed,
        page_location: window.location.href,
        source: pageSource,
        value: 1,
        currency: "INR",
      });
    } catch (err) {
      console.error("[Book Appointment] Exception during submit:", err);
      setSubmitting(false);
      setSubmitError("Network error. Please check your connection and try again, or call +91 99993 65655.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Book Free Consultation | ExpertAdvise.in Insurance Advisor"
        description="Book a free consultation with ExpertAdvise.in certified insurance advisors. Get personalized recommendations for health, term, life, car, and investment plans."
        keywords="book insurance consultation, free insurance advice, insurance advisor appointment, financial planner consultation"
        canonical={pageUrl}
        ogUrl={pageUrl}
        schemas={schemas}
      />
      <Navbar />

      <section className="pt-24 md:pt-32 pb-10 md:pb-14 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <Breadcrumbs
              items={[
                { name: "Home", href: "/" },
                { name: "Book Appointment" },
              ]}
              className="justify-center mb-4"
            />
            <span className="inline-block bg-cta-50 text-cta-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
              Book a Session
            </span>
            <h1 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4">
              Book Your Free Consultation
            </h1>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed">
              Speak with a certified insurance advisor. No pressure, no obligation — just honest, expert guidance
              tailored to your needs. Choose your preferred date and time.
            </p>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8">
            {/* Contact Info Sidebar */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl p-6 md:p-8 text-white">
                <h3 className="text-xl font-bold mb-5">Why Book With Us?</h3>
                <div className="space-y-4">
                  {[
                    { icon: CheckCircle, text: "100% Free consultation — no hidden charges" },
                    { icon: CheckCircle, text: "Certified IRDAI-licensed advisors" },
                    { icon: CheckCircle, text: "Unbiased recommendations across 25+ insurers" },
                    { icon: CheckCircle, text: "Lifetime claims support after purchase" },
                    { icon: CheckCircle, text: "Available 7 days a week, 9 AM - 8 PM" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <item.icon className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                      <span className="text-sm text-white/90">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <a href="tel:+919999365655" className="flex items-center gap-3 text-slate-700 hover:text-primary-600 transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center"><Phone className="w-4.5 h-4.5 text-primary-600" /></div>
                    <div><div className="text-xs text-slate-500">Call Us</div><div className="text-sm font-medium">+91 99993 65655</div></div>
                  </a>
                  <a href="mailto:info@expertadvise.in" className="flex items-center gap-3 text-slate-700 hover:text-primary-600 transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center"><Mail className="w-4.5 h-4.5 text-primary-600" /></div>
                    <div><div className="text-xs text-slate-500">Email</div><div className="text-sm font-medium">info@expertadvise.in</div></div>
                  </a>
                  <div className="flex items-center gap-3 text-slate-700">
                    <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center"><Clock className="w-4.5 h-4.5 text-primary-600" /></div>
                    <div><div className="text-xs text-slate-500">Working Hours</div><div className="text-sm font-medium">Mon - Sat: 9 AM - 8 PM</div></div>
                  </div>
                  <div className="flex items-start gap-3 text-slate-700">
                    <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center shrink-0"><MapPin className="w-4.5 h-4.5 text-primary-600" /></div>
                    <div><div className="text-xs text-slate-500">Head Office</div><div className="text-sm font-medium">Delhi NCR, India</div></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              {submitted ? (
                <div className="bg-white rounded-2xl border border-emerald-200 p-8 md:p-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Booking Confirmed!</h2>
                  <p className="text-slate-600 mb-6">Thank you, {formData.name}. Our advisor will call you at {formData.phone} to confirm your appointment on {formData.preferredDate || "your chosen date"}.</p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Link to="/" className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-3 rounded-full transition-colors whitespace-nowrap">
                      Back to Home
                    </Link>
                    <WhatsAppWidget
                      variant="success"
                      userPhone={formData.phone}
                      insuranceType={formData.insuranceNeed}
                    >
                      Continue on WhatsApp
                    </WhatsAppWidget>
                  </div>
                  <p className="text-xs text-slate-400 mt-3">
                    Our advisor will WhatsApp you at {formData.phone} to confirm your appointment
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 space-y-5">
                  <SupabaseConnectionStatus />
                  <h3 className="text-lg font-bold text-slate-900 mb-1">Appointment Details</h3>
                  <p className="text-sm text-slate-500 mb-4">Fill in your details and we will get back to you within 2 hours.</p>

                  {submitError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                      <div className="font-semibold mb-1">Submission failed</div>
                      <div className="text-xs font-mono break-all">{submitError}</div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5"><User className="w-4 h-4 text-primary-500" />Full Name *</label>
                      <input required name="name" value={formData.name} onChange={handleChange} type="text" placeholder="Enter your full name" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-primary-400 placeholder:text-slate-400" />
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5"><Phone className="w-4 h-4 text-primary-500" />Mobile Number *</label>
                      <input required name="phone" value={formData.phone} onChange={handleChange} type="tel" placeholder="+91 99999 99999" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-primary-400 placeholder:text-slate-400" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5"><Mail className="w-4 h-4 text-primary-500" />Email Address</label>
                      <input name="email" value={formData.email} onChange={handleChange} type="email" placeholder="you@example.com" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-primary-400 placeholder:text-slate-400" />
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5"><MapPin className="w-4 h-4 text-primary-500" />City *</label>
                      <input required name="city" value={formData.city} onChange={handleChange} type="text" placeholder="e.g. Delhi, Mumbai" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-primary-400 placeholder:text-slate-400" />
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5"><FileText className="w-4 h-4 text-primary-500" />Insurance Requirement *</label>
                    <select required name="insuranceNeed" value={formData.insuranceNeed} onChange={handleChange} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-primary-400 cursor-pointer">
                      <option value="health">Health Insurance</option>
                      <option value="term">Term Insurance</option>
                      <option value="life">Life Insurance</option>
                      <option value="car">Car Insurance</option>
                      <option value="travel">Travel Insurance</option>
                      <option value="corporate">Corporate / Group Health</option>
                      <option value="mutual-funds">Mutual Funds / SIP</option>
                      <option value="retirement">Retirement Planning</option>
                      <option value="financial">Financial Planning</option>
                      <option value="multiple">Multiple / Not Sure</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5"><Calendar className="w-4 h-4 text-primary-500" />Preferred Date</label>
                      <input name="preferredDate" value={formData.preferredDate} onChange={handleChange} type="date" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-primary-400" />
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5"><Clock className="w-4 h-4 text-primary-500" />Preferred Time</label>
                      <select name="preferredTime" value={formData.preferredTime} onChange={handleChange} className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-primary-400 cursor-pointer">
                        <option value="morning">Morning (9 AM - 12 PM)</option>
                        <option value="afternoon">Afternoon (12 PM - 4 PM)</option>
                        <option value="evening">Evening (4 PM - 8 PM)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1.5"><FileText className="w-4 h-4 text-primary-500" />Additional Message (Optional)</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} rows={3} maxLength={500} placeholder="Tell us more about your needs..." className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-primary-400 placeholder:text-slate-400 resize-none" />
                    <div className="text-xs text-slate-400 mt-1 text-right">{formData.message.length}/500</div>
                  </div>

                  <button type="submit" disabled={submitting} className="w-full bg-cta-600 hover:bg-cta-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-xl transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
                    {submitting ? (
                      <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>Book Free Consultation <ArrowRight className="w-4 h-4" /></>
                    )}
                  </button>

                  <p className="text-xs text-slate-400 text-center">
                    By submitting, you agree to our{" "}
                    <Link to="/privacy-policy" className="text-primary-600 hover:underline">Privacy Policy</Link>{" "}
                    and{" "}
                    <Link to="/terms" className="text-primary-600 hover:underline">Terms of Service</Link>.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
}